import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useApprovalForms, useCreateApproval } from '../hooks/useApprovalCenter';
import { createApprovalSchema } from '../schema/approvalCenterSchema';
import type { CreateApprovalFormValues } from '../schema/approvalCenterSchema';
import type { ApprovalFormItem, ApiError } from '../types/approvalCenter';

/**
 * ApprovalRequestNewPage — 결재 상신 폼
 * 라우트: /approval-center/new
 * 권한: APPROVAL_REQUEST (ROLE_USER+)
 */
export function ApprovalRequestNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formsQuery = useApprovalForms();
  const createMutation = useCreateApproval();

  const [selectedForm, setSelectedForm] = useState<ApprovalFormItem | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateApprovalFormValues>({
    resolver: zodResolver(createApprovalSchema),
    defaultValues: {
      formCode: '',
      title: '',
      summary: '',
      formData: {},
    },
  });

  const watchedFormCode = watch('formCode');

  // 양식 선택 시 selectedForm 업데이트
  useEffect(() => {
    if (!watchedFormCode || !formsQuery.data) {
      setSelectedForm(null);
      return;
    }
    const found = formsQuery.data.find((f) => f.formCode === watchedFormCode);
    setSelectedForm(found ?? null);
    // 폼 데이터 초기화
    setValue('formData', {});
  }, [watchedFormCode, formsQuery.data, setValue]);

  const onSubmit = useCallback(
    (values: CreateApprovalFormValues) => {
      setSubmitError(null);
      createMutation.mutate(
        {
          formCode: values.formCode,
          title: values.title,
          summary: values.summary,
          formData: values.formData ?? {},
        },
        {
          onSuccess: (data) => {
            navigate(`/approval-center/${data.id}`);
          },
          onError: (err) => {
            const apiErr = err as unknown as ApiError;
            const errKey = `approval.error.${apiErr.code}`;
            setSubmitError(t(errKey, { defaultValue: apiErr.message }));
          },
        }
      );
    },
    [createMutation, navigate, t]
  );

  const isLoading = formsQuery.isLoading;
  const isError = formsQuery.isError;
  const isPending = isSubmitting || createMutation.isPending;

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('approval.new.title')}
        breadcrumb={
          <span>
            <button
              type="button"
              onClick={() => navigate('/approval-center')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-sm)',
                padding: 0,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              ← {t('approval.inbox.title')}
            </button>
            {' / '}
            {t('approval.new.title')}
          </span>
        }
      />

      {/* Loading */}
      {isLoading && (
        <div style={{ padding: 'var(--spacing-6)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: '3px solid var(--color-surface-border)',
              borderTopColor: 'var(--color-brand-primary)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto var(--spacing-3)',
            }}
          />
          Loading...
        </div>
      )}

      {/* 오류 */}
      {isError && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <ErrorState
            title={t('approval.error.network')}
            message={(formsQuery.error as unknown as ApiError)?.message ?? t('approval.error.network')}
            onRetry={() => void formsQuery.refetch()}
          />
        </div>
      )}

      {/* 빈 상태 — 양식 0건 */}
      {!isLoading && !isError && (formsQuery.data?.length ?? 0) === 0 && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <EmptyState
            title="사용 가능한 결재 양식이 없습니다"
            description="관리자에게 문의하세요"
          />
        </div>
      )}

      {/* 기본 — 결재 상신 폼 */}
      {!isLoading && !isError && (formsQuery.data?.length ?? 0) > 0 && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-label={t('approval.new.title')}
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-background)',
            overflow: 'hidden',
            maxWidth: 720,
          }}
        >
          {/* 양식 선택 */}
          <div style={{ padding: 'var(--spacing-5)', borderBottom: '1px solid var(--color-surface-border)' }}>
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <label
                htmlFor="formCode"
                style={labelStyle}
              >
                {t('approval.new.field.form')}
                <span style={{ color: 'var(--color-danger)', marginLeft: 'var(--spacing-1)' }}>*</span>
              </label>
              <select
                id="formCode"
                {...register('formCode')}
                aria-required="true"
                aria-invalid={!!errors.formCode}
                aria-describedby={errors.formCode ? 'formCode-error' : undefined}
                disabled={isPending}
                style={{
                  ...inputStyle,
                  appearance: 'auto',
                  cursor: isPending ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">{t('approval.new.field.form')}</option>
                {formsQuery.data?.map((form) => (
                  <option key={form.formCode} value={form.formCode}>
                    {form.labelKo}
                  </option>
                ))}
              </select>
              {errors.formCode && (
                <p id="formCode-error" role="alert" style={errorStyle}>
                  {errors.formCode.message}
                </p>
              )}
            </div>

            {/* 결재선 미리보기 */}
            {selectedForm && (
              <div
                style={{
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-surface-muted)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <strong>{t('approval.new.field.line')}: </strong>
                {selectedForm.steps.map((step, i) => (
                  <span key={step.stepName}>
                    {i > 0 && ' → '}
                    {step.stepLabel || t(`approval.step.${step.stepName}`, { defaultValue: step.stepName })}
                    <span style={{ color: 'var(--color-text-muted)', marginLeft: 2 }}>
                      ({step.approverRole})
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* 기본 정보 */}
          <div style={{ padding: 'var(--spacing-5)', borderBottom: '1px solid var(--color-surface-border)' }}>
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <label htmlFor="title" style={labelStyle}>
                {t('approval.new.field.title')}
                <span style={{ color: 'var(--color-danger)', marginLeft: 'var(--spacing-1)' }}>*</span>
              </label>
              <input
                id="title"
                type="text"
                {...register('title')}
                aria-required="true"
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'title-error' : undefined}
                disabled={isPending}
                placeholder={t('approval.new.field.title')}
                style={inputStyle}
              />
              {errors.title && (
                <p id="title-error" role="alert" style={errorStyle}>
                  {errors.title.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="summary" style={labelStyle}>
                {t('approval.new.field.summary')}
              </label>
              <input
                id="summary"
                type="text"
                {...register('summary')}
                disabled={isPending}
                placeholder={t('approval.new.field.summary')}
                style={inputStyle}
              />
            </div>
          </div>

          {/* 동적 폼 필드 (양식별) */}
          {selectedForm && selectedForm.fields.length > 0 && (
            <div style={{ padding: 'var(--spacing-5)', borderBottom: '1px solid var(--color-surface-border)' }}>
              <h3
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-4)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  paddingBottom: 'var(--spacing-2)',
                }}
              >
                {selectedForm.labelKo} 본문
              </h3>
              {selectedForm.fields.map((field) => (
                <div key={field.key} style={{ marginBottom: 'var(--spacing-4)' }}>
                  <label
                    htmlFor={`formData.${field.key}`}
                    style={labelStyle}
                  >
                    {field.labelKo}
                    {field.required && (
                      <span style={{ color: 'var(--color-danger)', marginLeft: 'var(--spacing-1)' }}>*</span>
                    )}
                  </label>
                  <Controller
                    name={`formData.${field.key}` as `formData.${string}`}
                    control={control}
                    render={({ field: controllerField }) => {
                      if (field.type === 'textarea') {
                        return (
                          <textarea
                            id={`formData.${field.key}`}
                            {...controllerField}
                            value={String(controllerField.value ?? '')}
                            rows={3}
                            disabled={isPending}
                            aria-required={field.required}
                            placeholder={field.labelKo}
                            style={{ ...inputStyle, resize: 'vertical', height: 'auto' }}
                          />
                        );
                      }
                      if (field.type === 'number') {
                        return (
                          <input
                            id={`formData.${field.key}`}
                            type="number"
                            disabled={isPending}
                            aria-required={field.required}
                            placeholder={field.labelKo}
                            value={String(controllerField.value ?? '')}
                            onChange={(e) => controllerField.onChange(e.target.value ? Number(e.target.value) : '')}
                            style={inputStyle}
                          />
                        );
                      }
                      if (field.type === 'date') {
                        return (
                          <input
                            id={`formData.${field.key}`}
                            type="date"
                            {...controllerField}
                            value={String(controllerField.value ?? '')}
                            disabled={isPending}
                            aria-required={field.required}
                            style={inputStyle}
                          />
                        );
                      }
                      // text (default)
                      return (
                        <input
                          id={`formData.${field.key}`}
                          type="text"
                          {...controllerField}
                          value={String(controllerField.value ?? '')}
                          disabled={isPending}
                          aria-required={field.required}
                          placeholder={field.labelKo}
                          style={inputStyle}
                        />
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* 제출 오류 */}
          {submitError && (
            <div
              style={{ padding: 'var(--spacing-3) var(--spacing-5)' }}
            >
              <p
                role="alert"
                aria-live="assertive"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-danger)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-danger)',
                }}
              >
                {submitError}
              </p>
            </div>
          )}

          {/* 액션 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-4) var(--spacing-5)',
              background: 'var(--color-surface-muted)',
              borderTop: '1px solid var(--color-surface-border)',
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/approval-center')}
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('approval.new.button.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-5)',
                background: isPending ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                color: isPending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {isPending ? '...' : t('approval.new.button.submit')}
            </button>
          </div>
        </form>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          form { max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

// ─── 공통 스타일 ──────────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: 'var(--spacing-2)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  outline: 'none',
};

const errorStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};
